import React, { useEffect, useRef, useState } from 'react';

// Small SVG bar charts (no chart library). Series colors come from the
// validated categorical palette, assigned in fixed order by the caller (never by
// rank). Every chart has a legend (2+ series), hover tooltips, and the screen
// always shows the data table as well.
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'];

const INK = '#52514e';

// The width (px) available to a chart, kept current as the window resizes.
function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}
const GRID = '#e5e7eb';

// "Nice" axis ticks covering [min, max] (min <= 0 <= max).
function ticks(min, max, count = 4) {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || raw;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const out = [];
  for (let v = lo; v <= hi + step / 2; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

function Legend({ series, always }) {
  if (series.length < (always ? 1 : 2)) return null;
  return (
    <div className="chart-legend">
      {series.map((s) => (
        <span key={s.key} className="chart-legend-item">
          {s.kind === 'line'
            ? <span className="chart-swatch-line" style={{ background: s.color }}><span style={{ background: s.color }} /></span>
            : <span className="chart-swatch" style={{ background: s.color }} />}
          {s.label}
        </span>
      ))}
    </div>
  );
}

function Tooltip({ tip }) {
  if (!tip) return null;
  return (
    <div className="chart-tooltip" style={{ left: tip.x, top: tip.y }}>
      <div className="chart-tooltip-title">{tip.title}</div>
      {tip.lines.map((l) => (
        <div key={l.label} className="chart-tooltip-line">
          {l.color && <span className="chart-swatch" style={{ background: l.color }} />}
          <span>{l.label}</span><strong>{l.value}</strong>
        </div>
      ))}
    </div>
  );
}

// Path for a bar with 4px rounded corners on the end away from the baseline.
function barPath(x, y0, w, y1) {
  const up = y1 < y0;
  const h = Math.abs(y1 - y0);
  const r = Math.min(4, w / 2, h);
  const top = Math.min(y0, y1);
  if (h === 0) return '';
  if (up) {
    return `M${x},${y0} V${top + r} Q${x},${top} ${x + r},${top} H${x + w - r} Q${x + w},${top} ${x + w},${top + r} V${y0} Z`;
  }
  const bot = top + h;
  return `M${x},${y0} V${bot - r} Q${x},${bot} ${x + r},${bot} H${x + w - r} Q${x + w},${bot} ${x + w},${bot - r} V${y0} Z`;
}

// Bars on a left axis and lines on a right axis over the same categories
// (e.g. periods). Each side has its own scale and tick labels; either side may
// be empty. left / right = [{ key, label, color, values: number[], format }];
// leftFormat / rightFormat label each axis. longLabels: every category label,
// slanted and shortened (full label in the tooltip), e.g. for named groups.
export function ComboChart({ categories, left, right, leftFormat, rightFormat, leftTitle, rightTitle, height: plotHeight = 280, title, longLabels }) {
  const [tip, setTip] = useState(null);
  const [ref, avail] = useContainerWidth();
  const hasL = left.length > 0;
  const hasR = right.length > 0;
  // Slanted labels run down-left from their bar, so the first needs room on the left.
  const M = { top: 22, right: hasR ? 64 : 10, bottom: longLabels ? 140 : 34, left: longLabels ? 160 : hasL ? 64 : 10 };
  const height = plotHeight + M.bottom - 34;
  // Fill the available width; scroll sideways only when the bars need more.
  const W = Math.max(avail || 320, categories.length * (Math.max(left.length, 1) * 6 + 8) + M.left + M.right);
  const innerW = W - M.left - M.right;
  const innerH = height - M.top - M.bottom;
  const scale = (series) => {
    const all = series.flatMap((s) => s.values.map((v) => v || 0));
    const t = ticks(Math.min(0, ...all), Math.max(0, ...all));
    const lo = t[0];
    const hi = t[t.length - 1];
    return { t, y: (v) => M.top + innerH - (((v || 0) - lo) / (hi - lo || 1)) * innerH };
  };
  const L = scale(left);
  const R = scale(right);
  const grid = hasL ? L : R;
  const band = innerW / Math.max(categories.length, 1);
  const gap = 2;
  const barW = hasL ? Math.max(2, Math.min(28, (band * 0.8 - gap * (left.length - 1)) / left.length)) : 0;
  const groupW = barW * left.length + gap * (left.length - 1);
  const cx = (i) => M.left + i * band + band / 2;
  const labelEvery = longLabels ? 1 : Math.ceil(categories.length / Math.max(1, Math.floor(innerW / 64)));
  const short = (l) => (longLabels && l.length > 38 ? `${l.slice(0, 37)}…` : l);

  return (
    <div className="chart">
      {title && <div className="chart-title">{title}</div>}
      <Legend always series={[...left.map((s) => ({ ...s, kind: 'bar' })), ...right.map((s) => ({ ...s, kind: 'line' }))]} />
      <div className="chart-scroll" ref={ref} onMouseLeave={() => setTip(null)}>
        <svg width={W} height={height} role="img" aria-label={title || 'Chart'}>
          {grid.t.map((t) => (
            <line key={t} x1={M.left} x2={W - M.right} y1={grid.y(t)} y2={grid.y(t)} stroke={t === 0 ? '#9ca3af' : GRID} strokeWidth="1" />
          ))}
          {hasL && leftTitle && <text x={M.left - 6} y={12} fontSize="11" fontWeight="600" textAnchor="end" fill={INK}>{leftTitle}</text>}
          {hasR && rightTitle && <text x={W - M.right + 6} y={12} fontSize="11" fontWeight="600" textAnchor="start" fill={INK}>{rightTitle}</text>}
          {hasL && L.t.map((t) => (
            <text key={t} x={M.left - 6} y={L.y(t) + 4} fontSize="11" textAnchor="end" fill={INK}>{leftFormat(t, true)}</text>
          ))}
          {hasR && R.t.map((t) => (
            <text key={t} x={W - M.right + 6} y={R.y(t) + 4} fontSize="11" textAnchor="start" fill={INK}>{rightFormat(t, true)}</text>
          ))}
          {categories.map((c, i) => {
            const gx = cx(i) - groupW / 2;
            return (
              <g key={c.key}
                onMouseMove={(e) => setTip({
                  x: e.nativeEvent.offsetX + 12, y: e.nativeEvent.offsetY + 12, title: c.label,
                  lines: [...left, ...right].map((s) => ({ label: s.label, color: s.color, value: s.format(s.values[i] || 0) })),
                })}>
                <rect x={M.left + i * band} y={M.top} width={band} height={innerH} fill="transparent" />
                {left.map((s, k) => (
                  <path key={s.key} d={barPath(gx + k * (barW + gap), L.y(0), barW, L.y(s.values[i]))} fill={s.color} />
                ))}
                {i % labelEvery === 0 && (longLabels ? (
                  <text x={cx(i)} y={M.top + innerH + 12} fontSize="11" textAnchor="end" fill={INK}
                    transform={`rotate(-35 ${cx(i)} ${M.top + innerH + 12})`}>{short(c.label)}</text>
                ) : (
                  <text x={cx(i)} y={height - 14} fontSize="11" textAnchor="middle" fill={INK}>{c.label}</text>
                ))}
              </g>
            );
          })}
          {right.map((s) => (
            <g key={s.key} pointerEvents="none">
              <path d={s.values.map((v, i) => `${i ? 'L' : 'M'}${cx(i)},${R.y(v)}`).join(' ')} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" />
              {s.values.map((v, i) => <circle key={categories[i] ? categories[i].key : i} cx={cx(i)} cy={R.y(v)} r="4" fill={s.color} stroke="#ffffff" strokeWidth="2" />)}
            </g>
          ))}
        </svg>
        <Tooltip tip={tip} />
      </div>
    </div>
  );
}
